import { computed, reactive, toValue } from 'vue';
import i18next from '@/config/i18n';
import axios from '@/plugins/axios';
import eventBus from '@/eventBus';
import { useSnackbar } from '@/composables/useSnackbar';
import {
  FEATURE_HTML_EXPORT,
  FEATURE_PPTX_EXPORT,
  FEATURE_SLIDES_EXPORT,
} from '@/config/features';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

// ──────────────────────────────────────────────────────────────────────────
// PPTX export RUNNER. Since SYN-1528 the deck is rendered by the BACKEND
// (Temporal worker), not in this tab: clicking "Exportar Relatório" only asks
// the API to produce it. The user may close the tab — the render finishes anyway
// and the artifact is cached, so coming back and clicking again returns the file
// without re-rendering.
//
// The tracked state is a REGISTRY, one entry per creative (SYN-1642). It used to
// be a single module-level object, and that is precisely why exporting one
// creative disabled every other creative's button: `active` was global, so every
// button that read it went into the loading state together.
//
// Keyed by experiment id, not by report id: the button has to look its state up
// at click time, before any report exists. The consequence is one export per
// creative at a time — asking for the .pptx and the Slides deck of the SAME
// creative simultaneously still waits, which is what the single button already
// implied and nobody asked to change.
//
// While the tab stays open each entry is tracked through the same `/ws` channel
// the processing bell uses (`report_status_update`), with a slow HTTP poll as a
// safety net for a dropped socket. Progress is surfaced in QueueStatus.
//
// Everything lives at module scope so tracking survives route changes. A full
// page RELOAD does drop it — hydrate() rebuilds every entry from the server.
//
// The client-side generators (useImageReportExportCanvas / useVideoReportExport
// / pptxCanvas) are no longer called from here. They stay in the repo until the
// retirement pass (SYN-1530).
// ──────────────────────────────────────────────────────────────────────────

const NS = 'components.experiment.pptxReportExport';

// The destinations a report can be delivered to (SYN-1622, SYN-2111). They are
// different ARTIFACTS, not several ways of fetching one: the Slides deck is
// rendered without embedded mp4 and the HTML package is a .zip with its own
// assets, so having one says nothing about having the others. Since SYN-2721 the
// HTML one is consumed by OPENING its public page (`public_url`), never by
// downloading — the .zip is a button on that page.
export const DESTINATIONS = { DOWNLOAD: 'download', HTML: 'html', SLIDES: 'google_slides' };

// SYN-2111 — qual feature comercial destranca cada destino. Fonte única do
// cadeado por item do menu E do 403 defensivo: sem o mapa o dialog de upsell
// nomeava `pptx_export` para qualquer destino recusado, o que é mentira para
// quem foi barrado no HTML ou no Slides.
export const DESTINATION_FEATURES = {
  [DESTINATIONS.DOWNLOAD]: FEATURE_PPTX_EXPORT,
  [DESTINATIONS.HTML]: FEATURE_HTML_EXPORT,
  [DESTINATIONS.SLIDES]: FEATURE_SLIDES_EXPORT,
};

// SYN-1865 — retorno de `run()` quando o backend recusa por falta de
// entitlement (403 `reason: 'feature_not_available'`). Não é falha de export:
// o chamador abre o dialog de upsell em vez do snackbar de erro.
export const FEATURE_LOCKED = 'feature_locked';

// The server reports discrete phases, not a percentage. These are the anchors
// the smoothing ticker eases toward — deliberately low, because the honest
// answer to "how far along is it" is unknown and overpromising then stalling at
// 90% reads worse than climbing slowly.
const PROGRESS_BY_STATUS = { queued: 6, processing: 30 };

// Not merely a safety net for a dropped socket: there is a real race where a
// fast render pushes `completed` BEFORE this tab finishes subscribing, and the
// poll is what recovers that. Long enough not to hammer the API through a
// 45-minute video render.
const POLL_INTERVAL_MS = 15000;

const TERMINAL = new Set(['completed', 'failed']);

function blankEntry(experimentId) {
  return {
    experimentId,
    active: false, // generating right now
    progress: 0, // 0-100 (smoothed — the server gives phases, not a %)
    mediaType: 'image', // 'video' | 'image'
    experimentName: '',
    result: null, // null | 'success' | 'error' | 'cancelled'
    read: true, // false when a fresh result hasn't been seen in the bell yet
    finishedAt: 0,
    startedAt: 0, // orders the bell when several exports are in flight
    reportId: null,
    degradedSlides: 0, // evidence slides delivered without the engagement curve
    // Set only when the state came from hydrate(): the render finished while this
    // tab was closed, so no download was ever triggered and the bell has to offer
    // one. Null during a live run, where the download fires automatically.
    pendingUrl: null,
    destination: DESTINATIONS.DOWNLOAD,
    // The deck in Google Slides. Unlike pendingUrl this is NOT signed and does not
    // expire in minutes, so it stays valid in the bell for as long as the row does.
    slidesUrl: null,
    // SYN-2721 — the HTML report's public page (`/r/:token`). Like slidesUrl it
    // does not expire on its own, and the API hands it out even while queued.
    publicUrl: null,
  };
}

const entries = reactive({});

// What a button reads for a creative that has never exported. Frozen so a
// consumer cannot mutate the shared fallback into looking active.
const IDLE = Object.freeze(blankEntry(null));

// Interval ids, the WS handler and run()'s resolver, deliberately OUTSIDE the
// reactive registry: none of them should ever trigger a re-render, and a
// reactive setInterval id is a footgun.
const runtime = new Map();

function ensureEntry(experimentId) {
  if (!entries[experimentId]) entries[experimentId] = blankEntry(experimentId);
  if (!runtime.has(experimentId)) {
    runtime.set(experimentId, { ticker: null, poll: null, wsHandler: null, settle: null, settling: false, raw: 0 });
  }
  return entries[experimentId];
}

// ── progress smoothing ────────────────────────────────────────────────────
// Eases asymptotically toward the current phase anchor + a margin, snapping up
// to truth and never backward, so the bell never looks frozen during a render
// that legitimately takes minutes. One ticker per entry: with the cap at three
// in flight (SYN-1643) that is at most three timers.

function stopTicker(rt) {
  if (rt.ticker) { clearInterval(rt.ticker); rt.ticker = null; }
}

function startTicker(entry, rt) {
  stopTicker(rt);
  entry.progress = Math.max(entry.progress, 1);
  rt.ticker = setInterval(() => {
    if (rt.raw > entry.progress) entry.progress = rt.raw;
    const cap = Math.min(95, rt.raw + 45);
    if (entry.progress < cap) {
      entry.progress = Math.min(cap, entry.progress + Math.max(0.08, (cap - entry.progress) * 0.012));
    }
  }, 120);
}

// ── tracking (WS + poll fallback) ─────────────────────────────────────────

function stopTracking(rt) {
  if (rt.wsHandler) { eventBus.off('websocket-message', rt.wsHandler); rt.wsHandler = null; }
  if (rt.poll) { clearInterval(rt.poll); rt.poll = null; }
}

export function triggerDownload(url) {
  // The signed URL already carries Content-Disposition: attachment, so the
  // browser saves instead of navigating. An <a> click rather than
  // location.href, so a missing header cannot take the user out of the SPA.
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function openSlides(url) {
  // noopener: the deck opens in a tab that cannot reach back into this one.
  window.open(url, '_blank', 'noopener');
}

// SYN-2721 — a página pública do relatório HTML. Mesmo `noopener` do Slides.
export function openPublicReport(url) {
  window.open(url, '_blank', 'noopener');
}

function finish(entry, rt, result, errorMessage = null, degradedSlides = 0) {
  if (!entry.active) return; // idempotent: WS and poll can both land the news
  stopTicker(rt);
  stopTracking(rt);
  rt.settling = false;

  entry.pendingUrl = null; // a live run downloaded already; nothing left to offer
  entry.active = false;
  if (result === 'success') entry.progress = 100;
  entry.result = result;
  entry.degradedSlides = degradedSlides;
  entry.read = false;
  entry.finishedAt = Date.now();

  const { showSnackbar } = useSnackbar();
  if (result === 'success') {
    // A video deck can succeed and still ship one or more evidence slides
    // without the neural curve (composite failed / no ffmpeg / map missing).
    // Warn rather than report a clean success, so a weaker deck is never handed
    // over silently — the backend counts these for us (SYN-1599).
    if (degradedSlides > 0) {
      // A variante do HTML diz a mesma coisa sem a palavra "slide": o pacote é um
      // documento corrido, e prometer slides que ele não tem é copy mentirosa. O
      // parâmetro segue chamando `slides` — é o nome do dado que o backend conta.
      const isHtml = entry.destination === DESTINATIONS.HTML;
      const message = i18next.t(
        isHtml
          ? `${NS}.snackbar.successDegradedHtml`
          : `${NS}.snackbar.successDegraded`,
        { slides: degradedSlides },
      );
      // Sem a aba do clique, o aviso também é o "Abrir" (ver o ramo do HTML abaixo).
      if (isHtml && !rt.tabOpened) showSnackbar(message, 'warning', 7000, undefined, openAction(entry));
      else showSnackbar(message, 'warning', 7000);
    } else if (entry.destination === DESTINATIONS.SLIDES) {
      // Different copy on purpose: nothing landed in Downloads, and what the user
      // has to do next is open the link the bell is now offering.
      showSnackbar(i18next.t(`${NS}.snackbar.successSlides`), 'success');
    } else if (entry.destination === DESTINATIONS.HTML) {
      // SYN-2721: nada caiu em Downloads. Com a aba aberta no clique, o relatório
      // já está nela (a página pública vira o relatório sozinha); sem ela (pop-up
      // bloqueado, ou o render terminou depois de um F5) o snackbar traz o "Abrir"
      // — clicar nele é um gesto novo, que o bloqueador deixa passar.
      if (rt.tabOpened) {
        showSnackbar(i18next.t(`${NS}.snackbar.successHtml`), 'success');
      } else {
        showSnackbar(i18next.t(`${NS}.snackbar.successHtmlOpen`), 'success', 10000, undefined, openAction(entry));
      }
    } else {
      showSnackbar(i18next.t(`${NS}.snackbar.success`), 'success');
    }
  } else if (result === 'cancelled') showSnackbar(i18next.t(`${NS}.snackbar.cancelled`), 'info');
  else showSnackbar(errorMessage || i18next.t(`${NS}.snackbar.error`), 'error');

  const settle = rt.settle;
  rt.settle = null;
  if (settle) settle(result === 'success');
}

// O "Abrir" do snackbar do HTML. A URL é a da entrada no momento do clique.
function openAction(entry) {
  return {
    label: i18next.t(`${NS}.snackbar.open`),
    onClick: () => openPublicReport(entry.publicUrl),
  };
}

async function applyStatus(entry, rt, status, errorMessage = null) {
  if (!entry.active || rt.settling) return;

  if (!TERMINAL.has(status)) {
    // queued → processing just moves the progress anchor.
    rt.raw = Math.max(rt.raw, PROGRESS_BY_STATUS[status] ?? rt.raw);
    return;
  }

  rt.settling = true;

  if (status === 'failed') { finish(entry, rt, 'error', errorMessage); return; }

  // Completed: re-read the row for a FRESH signed URL. The push does not carry
  // one because signed URLs expire in minutes — by the time a client acted on a
  // stored one it could already be dead.
  try {
    const { data } = await axios.get(`/reports/${entry.reportId}`);
    if (entry.destination === DESTINATIONS.HTML) {
      // SYN-2721: nada a baixar — o relatório se consome pela página pública.
      // Sem `?.` de propósito: um corpo nulo lança aqui e cai no catch abaixo,
      // que fecha como erro com a mesma mensagem padrão.
      if (data.public_url) {
        entry.publicUrl = data.public_url;
        finish(entry, rt, 'success', null, data.degraded_slides || 0);
      } else {
        finish(entry, rt, 'error');
      }
      return;
    }
    if (entry.destination === DESTINATIONS.SLIDES) {
      // Deliberately NOT opening a tab here: this lands minutes after the click,
      // and window.open outside a user gesture is what popup blockers exist for.
      // The bell carries the link instead.
      if (data?.slides_url) {
        entry.slidesUrl = data.slides_url;
        finish(entry, rt, 'success', null, data.degraded_slides || 0);
      } else {
        finish(entry, rt, 'error');
      }
      return;
    }
    if (data?.download_url) {
      triggerDownload(data.download_url);
      finish(entry, rt, 'success', null, data.degraded_slides || 0);
    } else {
      // Completed with no URL means the artifact is gone (retention) or signing
      // failed. Either way there is nothing to hand the user right now.
      finish(entry, rt, 'error');
    }
  } catch (e) {
    finish(entry, rt, 'error', apiErrorMessage(e, `${NS}.snackbar.error`, i18next.t.bind(i18next)));
  }
}

function startTracking(entry, rt) {
  rt.wsHandler = (msg) => {
    if (msg?.type !== 'report_status_update') return;
    if (String(msg.report_id) !== String(entry.reportId)) return;
    applyStatus(entry, rt, msg.status, msg.error_message);
  };
  eventBus.on('websocket-message', rt.wsHandler);

  rt.poll = setInterval(async () => {
    // rt.settling too: a tick that fires while the WS push is already fetching the
    // signed URL would issue a second, pointless GET.
    if (!entry.active || rt.settling) return;
    try {
      const { data } = await axios.get(`/reports/${entry.reportId}`);
      applyStatus(entry, rt, data?.status, data?.error_message);
    } catch {
      // A failed poll is not a failed report — the WS is the primary signal and
      // the next tick retries. Giving up here would strand a healthy render.
    }
  }, POLL_INTERVAL_MS);
}

// ── hydration from the server ─────────────────────────────────────────────
// The registry is module state, so a page RELOAD erases it. That used to make
// the feature lie: SYN-1528 tells the user to close the tab, and coming back
// showed no progress and no notification even though the worker was still
// rendering. /reports/my-queue is the server-side truth the bell rebuilds from,
// exactly like /jobs/my-queue does for the analysis queue (SYN-1600).
async function hydrate() {
  let rows;
  try {
    ({ data: rows } = await axios.get('/reports/my-queue'));
  } catch {
    return; // bell hydration is best-effort; a failure must not break the menu
  }
  if (!Array.isArray(rows)) return;

  const seen = new Set();
  for (const row of rows) { // server orders newest first
    const key = row?.experiment_id;
    if (!key || seen.has(key)) continue;
    // Only the newest row per creative: the registry holds one entry per
    // creative, so an older report of the same one has nowhere to go.
    seen.add(key);

    const existing = entries[key];
    if (existing?.active) continue; // a live run in this tab owns its own entry

    const entry = ensureEntry(key);
    const rt = runtime.get(key);
    entry.reportId = row.id;
    entry.experimentName = row.experiment_name || '';
    entry.mediaType = row.media_type || 'image';
    entry.degradedSlides = row.degraded_slides || 0;
    entry.destination = row.destination || DESTINATIONS.DOWNLOAD;
    entry.startedAt = Date.parse(row.created_at) || Date.now();

    if (!TERMINAL.has(row.status)) {
      // Still rendering: pick the tracking back up so the bar moves again.
      entry.result = null;
      entry.read = true;
      entry.progress = 0;
      entry.active = true;
      entry.pendingUrl = null;
      entry.slidesUrl = null;
      entry.publicUrl = null;
      rt.tabOpened = false; // nenhuma aba desta página espera por este render
      rt.raw = PROGRESS_BY_STATUS[row.status] ?? PROGRESS_BY_STATUS.queued;
      rt.settling = false;
      startTicker(entry, rt);
      startTracking(entry, rt);
      continue;
    }

    // Finished while the tab was closed. Deliberately NOT downloading here: a file
    // landing in Downloads because a page was opened is startling. The bell offers
    // the download instead, and the URL is fresh because the server just signed it.
    entry.result = row.status === 'completed' ? 'success' : 'error';
    // O HTML nunca oferece download no sino (SYN-2721): o `.zip` mora na página.
    entry.pendingUrl = entry.destination === DESTINATIONS.HTML ? null : (row.download_url || null);
    entry.slidesUrl = row.slides_url || null;
    entry.publicUrl = row.public_url || null;
    entry.read = !!row.read;
    entry.finishedAt = Date.now();
  }
}

async function markSeenOnServer(reportId) {
  if (!reportId) return;
  try {
    await axios.patch(`/reports/${reportId}/read`);
  } catch {
    // Losing the flag only means the bell re-announces it later — not worth
    // surfacing an error to the user over.
  }
}

export function usePptxExportStatus(experimentId = null) {
  // The creative this caller is about. A getter/ref is accepted so the export
  // button follows its own `experiment` prop changing under it without
  // re-instantiating anything.
  const state = computed(() => entries[toValue(experimentId)] ?? IDLE);

  // What the bell renders: every tracked export, newest first. Reversed by
  // startedAt rather than insertion order, because hydrate() rebuilds the
  // registry in the server's order while live runs append in click order.
  const exports = computed(
    () => Object.values(entries).sort((a, b) => b.startedAt - a.startedAt),
  );

  const activeCount = computed(() => exports.value.filter((e) => e.active).length);
  const unreadCount = computed(
    () => exports.value.filter((e) => e.result && !e.read).length,
  );

  // Fire-and-forget from the caller — returns a promise that settles when the
  // report does, but the UI does not need to await it. Guards against a second
  // export OF THE SAME CREATIVE; other creatives are free to start (SYN-1642).
  async function run({ experiment, destination = DESTINATIONS.DOWNLOAD,
                       maxConcurrent = null } = {}) {
    const key = experiment?.id;
    // No creative, no entry to track it in — and the API would reject it anyway.
    if (!key) return false;

    const entry = ensureEntry(key);
    const rt = runtime.get(key);
    if (entry.active) return false;

    // SYN-2721 — a aba do relatório HTML abre AQUI, síncrona, antes de qualquer
    // await: o bloqueador de pop-up só deixa passar `window.open` dentro do gesto
    // do usuário (o clique no menu ou no "Gerar" do dialog), e o POST leva tempo.
    // Sem `noopener` de propósito — com ele o open devolve null e não haveria como
    // levar a aba ao link; o opener é cortado à mão assim que o link chega.
    // `null` = pop-up bloqueado: o export segue e o fim oferece "Abrir".
    // `rt.tabOpened` nasce aqui (e no hydrate), antes de qualquer leitura.
    const tab = destination === DESTINATIONS.HTML ? window.open('', '_blank') : null;
    rt.tabOpened = false;

    // Kept so a refusal can put the entry back exactly as it was. Being capped
    // must not erase a finished export the bell is still offering.
    const before = { ...entry };

    entry.active = true;
    entry.progress = 0;
    entry.mediaType = experiment?.media_type || 'image';
    entry.experimentName = experiment?.name || '';
    entry.result = null;
    entry.read = true;
    entry.reportId = null;
    entry.degradedSlides = 0;
    entry.destination = destination;
    entry.slidesUrl = null;
    entry.pendingUrl = null;
    entry.publicUrl = null;
    entry.startedAt = Date.now();
    rt.raw = PROGRESS_BY_STATUS.queued;
    rt.settling = false;

    startTicker(entry, rt);

    let data;
    try {
      const response = await axios.post('/reports', {
        experiment_id: key,
        // The deck follows the UI language, matching the insights the user is
        // reading on screen. The API collapses anything unsupported to 'pt'.
        language: i18next.resolvedLanguage || i18next.language || null,
        destination,
        // Nothing else travels: since SYN-1637 the deck's folder is shared by
        // link, so the export carries no identity. The API now REJECTS a stray
        // google_email instead of ignoring it, which is why it cannot linger here.
      });
      data = response.data;
    } catch (e) {
      // Nada foi enfileirado (ou o POST falhou): a aba vazia não tem o que mostrar.
      tab?.close();
      if (e?.response?.status === 429) {
        // The cap (SYN-1643), not a failure: nothing was queued, so leaving an
        // error row in the bell would report a breakage that did not happen.
        // Roll the entry back to whatever it was and say why in a snackbar.
        // Sem resolver promessa nenhuma: rt.settle só existe depois que o
        // rastreamento começa, e aqui o POST nem passou. rt.settling também não
        // precisa de reset — run() já o zerou antes de postar.
        stopTicker(rt);
        stopTracking(rt);
        Object.assign(entry, before);
        // Diz o número quando o chamador sabe qual é (vem de /reports/destinations,
        // única fonte do teto). Quando não sabe — a consulta ao ambiente falhou —
        // avisa sem número, em vez de inventar um que pode estar errado.
        const message = Number.isInteger(maxConcurrent)
          ? i18next.t(`${NS}.snackbar.limitReached`, { max: maxConcurrent })
          : i18next.t(`${NS}.snackbar.limitReachedUnknown`);
        useSnackbar().showSnackbar(message, 'warning', 7000);
        return false;
      }
      const detail = e?.response?.data?.detail;
      // SYN-1865 (RF7) — feature não contratada. Como o 429, nada foi
      // enfileirado: reverter a entrada e devolver o marcador para o chamador
      // abrir o dialog de upsell. Nunca cair no snackbar: o `detail` do guard é
      // um OBJETO (e a mensagem dentro dele é pt-BR crua, não traduzida).
      if (e?.response?.status === 403 && detail?.reason === 'feature_not_available') {
        stopTicker(rt);
        stopTracking(rt);
        Object.assign(entry, before);
        return FEATURE_LOCKED;
      }
      // `detail` estruturado nunca vira "[object Object]": o helper traduz o `code`
      // ou usa o `message` (SYN-2738).
      finish(entry, rt, 'error', apiErrorMessage(e, `${NS}.snackbar.error`, i18next.t.bind(i18next)));
      return false;
    }

    entry.reportId = data?.id || null;

    if (destination === DESTINATIONS.HTML) {
      // O link existe desde a fila (o contrato manda `public_url` mesmo em
      // QUEUED), então a aba vai direto para a página pública, que espera o
      // render sozinha. Sem link, ou com o render já falho, a aba fecha.
      if (data?.public_url && data.status !== 'failed' && entry.reportId) {
        entry.publicUrl = data.public_url;
        if (tab) {
          tab.opener = null;
          tab.location.replace(data.public_url);
          rt.tabOpened = true;
        }
      } else {
        tab?.close();
        finish(entry, rt, 'error', data?.error_message);
        return false;
      }
      if (data.status === 'completed') {
        finish(entry, rt, 'success', null, data.degraded_slides || 0);
        return true;
      }
    }

    // Cache hit: the artifact already existed for this analysis version.
    if (data?.status === 'completed' && data?.slides_url) {
      // Straight after the click, so the user gesture still allows a new tab —
      // which is exactly why the async path (applyStatus) does not do this.
      entry.slidesUrl = data.slides_url;
      openSlides(data.slides_url);
      finish(entry, rt, 'success', null, data.degraded_slides || 0);
      return true;
    }
    if (data?.status === 'completed' && data?.download_url) {
      triggerDownload(data.download_url);
      finish(entry, rt, 'success', null, data.degraded_slides || 0);
      return true;
    }
    if (data?.status === 'failed') {
      finish(entry, rt, 'error', data?.error_message);
      return false;
    }
    if (!entry.reportId) {
      finish(entry, rt, 'error');
      return false;
    }

    rt.raw = Math.max(rt.raw, PROGRESS_BY_STATUS[data.status] ?? PROGRESS_BY_STATUS.queued);
    startTracking(entry, rt);

    return await new Promise((resolve) => { rt.settle = resolve; });
  }

  /** The entry a bell action refers to, defaulting to this caller's creative. */
  function target(key) {
    const id = key ?? toValue(experimentId);
    const entry = entries[id];
    return entry ? [entry, runtime.get(id), id] : [null, null, id];
  }

  function cancel(key) {
    // Does NOT cancel the render: the workflow keeps running server-side and
    // the artifact lands in the cache, so a later click returns the finished
    // file without re-rendering. This only stops tracking it here.
    const [entry, rt] = target(key);
    if (entry) finish(entry, rt, 'cancelled');
  }

  // Both also tell the SERVER the news was seen. Without that the next hydrate()
  // would re-announce the same finished export on every page load.
  function markRead(key) {
    const [entry] = target(key);
    if (!entry) return Promise.resolve();
    entry.read = true;
    return markSeenOnServer(entry.reportId);
  }

  function dismiss(key) {
    const [entry, rt, id] = target(key);
    if (!entry) return Promise.resolve();
    // Tracking is stopped BEFORE the entry goes: dropping it while a ticker and a
    // bus listener still point at it would leave both running with nothing left
    // to turn them off. The bell only offers dismiss on a finished export, so this
    // is belt-and-braces — the kind that costs one line.
    stopTicker(rt);
    stopTracking(rt);
    const pending = markSeenOnServer(entry.reportId);
    // Dropped from the registry rather than blanked: with one entry per creative
    // a dismissed export has nothing left to say, and keeping a hollow row would
    // leave the bell rendering an empty item.
    delete entries[id];
    runtime.delete(id);
    return pending;
  }

  // Glancing at the bell silences finished exports — except the ones still
  // holding an action, which would otherwise lose their badge before the user
  // ever clicked the download.
  function markSeen() {
    return Promise.all(
      exports.value
        .filter((e) => e.result && !e.read && !e.pendingUrl)
        .map((e) => markRead(e.experimentId)),
    );
  }

  // Only reachable for a report hydrated as finished — a live run downloads on
  // its own. Re-reads the row instead of trusting entry.pendingUrl, because a
  // signed URL expires in minutes and the bell may have sat open for longer.
  async function downloadPending(key) {
    const [entry] = target(key);
    if (!entry?.reportId) return false;
    try {
      const { data } = await axios.get(`/reports/${entry.reportId}`);
      if (!data?.download_url) {
        // The artifact is gone (90-day retention) or signing failed. Drop the
        // offer so the button stops promising a file it cannot deliver and goes
        // back to "generate" — clicking that re-renders it.
        entry.pendingUrl = null;
        return false;
      }
      triggerDownload(data.download_url);
    } catch {
      // A network blip is not a missing artifact — keep the offer standing.
      return false;
    }
    await markRead(entry.experimentId);
    return true;
  }

  // The bell's counterpart to downloadPending. No re-read here: unlike a signed
  // URL, the Drive link does not expire on its own — access is the Drive
  // permission, and the row only carries the link while the deck should exist.
  async function openPendingSlides(key) {
    const [entry] = target(key);
    if (!entry?.slidesUrl) return false;
    openSlides(entry.slidesUrl);
    await markRead(entry.experimentId);
    return true;
  }

  // SYN-2721 — o "Abrir" do sino para o relatório HTML. Sem reler a linha, como
  // o Slides: o link público não expira sozinho, e se o artefato tiver expirado
  // é a própria página que diz isso.
  async function openPendingHtml(key) {
    const [entry] = target(key);
    if (!entry?.publicUrl) return false;
    openPublicReport(entry.publicUrl);
    await markRead(entry.experimentId);
    return true;
  }

  return {
    state,
    exports,
    activeCount,
    unreadCount,
    run,
    cancel,
    markRead,
    markSeen,
    dismiss,
    hydrate,
    downloadPending,
    openPendingSlides,
    openPendingHtml,
  };
}
